import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendIcon,
  AccessTime as ClockIcon,
  CheckCircle as ResolvedIcon,
  HourglassTop as ProgressIcon,
  Inbox as OpenIcon,
  PriorityHigh as UrgentIcon,
  Assignment as TotalIcon,
} from '@mui/icons-material';
import api, { extractData } from '../../utils/api';

interface Stats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  urgent: number;
  high: number;
  external: number;
  last24h: number;
  last7d: number;
}

type TimePeriod = '7d' | '30d' | '90d';

const HelpdeskReports: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState<TimePeriod>('30d');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, ticketsRes] = await Promise.all([
        api.get('/support/stats'),
        api.get('/support/tickets?limit=100'),
      ]);
      setStats(extractData(statsRes));
      const tData = extractData(ticketsRes) as { tickets?: any[] };
      setTickets(tData.tickets || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Compute report metrics from tickets
  const metrics = useMemo(() => {
    if (!tickets.length || !stats) return null;

    const now = new Date();
    const periodMs = period === '7d' ? 7 * 86400000 : period === '30d' ? 30 * 86400000 : 90 * 86400000;
    const cutoff = new Date(now.getTime() - periodMs);

    const periodTickets = tickets.filter((t: any) => new Date(t.createdAt) >= cutoff);
    const resolvedTickets = periodTickets.filter((t: any) => t.status === 'resolved' || t.status === 'closed');

    // Resolution times (only for resolved tickets with resolvedAt)
    const resTimes = resolvedTickets
      .filter((t: any) => t.resolvedAt)
      .map((t: any) => (new Date(t.resolvedAt).getTime() - new Date(t.createdAt).getTime()) / 3600000);
    const avgResTime = resTimes.length > 0 ? resTimes.reduce((a, b) => a + b, 0) / resTimes.length : 0;

    // Category breakdown
    const categoryMap: Record<string, number> = {};
    periodTickets.forEach((t: any) => {
      categoryMap[t.category || 'general'] = (categoryMap[t.category || 'general'] || 0) + 1;
    });

    // Priority breakdown
    const priorityMap: Record<string, number> = {};
    periodTickets.forEach((t: any) => {
      priorityMap[t.priority || 'medium'] = (priorityMap[t.priority || 'medium'] || 0) + 1;
    });

    // Status breakdown
    const statusMap: Record<string, number> = {};
    periodTickets.forEach((t: any) => {
      statusMap[t.status || 'open'] = (statusMap[t.status || 'open'] || 0) + 1;
    });

    // Daily trend (last N days)
    const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
    const dailyCreated: number[] = new Array(Math.min(days, 30)).fill(0);
    const dailyResolved: number[] = new Array(Math.min(days, 30)).fill(0);
    const stepMs = periodMs / Math.min(days, 30);
    periodTickets.forEach((t: any) => {
      const idx = Math.min(Math.floor((now.getTime() - new Date(t.createdAt).getTime()) / stepMs), dailyCreated.length - 1);
      if (idx >= 0 && idx < dailyCreated.length) dailyCreated[dailyCreated.length - 1 - idx]++;
    });
    resolvedTickets.forEach((t: any) => {
      const idx = Math.min(Math.floor((now.getTime() - new Date(t.resolvedAt || t.updatedAt).getTime()) / stepMs), dailyResolved.length - 1);
      if (idx >= 0 && idx < dailyResolved.length) dailyResolved[dailyResolved.length - 1 - idx]++;
    });

    return {
      periodTotal: periodTickets.length,
      periodResolved: resolvedTickets.length,
      resolutionRate: periodTickets.length > 0 ? Math.round((resolvedTickets.length / periodTickets.length) * 100) : 0,
      avgResolutionHours: Math.round(avgResTime * 10) / 10,
      categoryMap,
      priorityMap,
      statusMap,
      dailyCreated,
      dailyResolved,
    };
  }, [tickets, stats, period]);

  const priorityColors: Record<string, string> = { urgent: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
  const statusColors: Record<string, string> = { open: '#3b82f6', in_progress: '#f59e0b', resolved: '#10b981', closed: '#6b7280' };

  const maxBarValue = (data: number[]) => Math.max(...data, 1);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Reports & Analytics</Typography>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value as TimePeriod)} sx={{ fontSize: '0.85rem', borderRadius: '8px' }}>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchData}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 3, mt: 1 }}>{error}</Alert>}

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Summary Cards */}
        {stats && metrics && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
              {[
                { label: 'Total Tickets', value: stats.total, icon: <TotalIcon />, color: '#6366f1' },
                { label: 'Open', value: stats.open, icon: <OpenIcon />, color: '#3b82f6' },
                { label: 'In Progress', value: stats.inProgress, icon: <ProgressIcon />, color: '#f59e0b' },
                { label: 'Resolved', value: stats.resolved, icon: <ResolvedIcon />, color: '#10b981' },
                { label: 'Period Tickets', value: metrics.periodTotal, icon: <TrendIcon />, color: '#8b5cf6' },
                { label: 'Resolution Rate', value: `${metrics.resolutionRate}%`, icon: <ResolvedIcon />, color: '#14b8a6' },
                { label: 'Avg Resolution', value: `${metrics.avgResolutionHours}h`, icon: <ClockIcon />, color: '#f97316' },
                { label: 'Urgent', value: stats.urgent, icon: <UrgentIcon />, color: '#ef4444' },
              ].map((card, i) => (
                <Paper key={i} elevation={0} sx={{ p: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: `${card.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.color }}>
                    {card.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>{card.label}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#1e293b' }}>{card.value}</Typography>
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Charts Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2, mb: 3 }}>
              {/* Ticket Trend Bar Chart */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>Ticket Trend</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 140 }}>
                  {metrics.dailyCreated.map((val, i) => {
                    const maxV = maxBarValue(metrics.dailyCreated);
                    return (
                      <Tooltip key={i} title={`Created: ${val}`}>
                        <Box sx={{
                          flex: 1, borderRadius: '4px 4px 0 0',
                          bgcolor: '#6366f1',
                          height: `${Math.max((val / maxV) * 100, 4)}%`,
                          minHeight: 4,
                          transition: 'height 0.3s',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 },
                        }} />
                      </Tooltip>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af' }}>Oldest</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af' }}>Now</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: '#6366f1' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>Created</Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Status Breakdown */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>By Status</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Object.entries(metrics.statusMap).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
                    const total = Object.values(metrics.statusMap).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <Box key={status}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, textTransform: 'capitalize' }}>{status.replace('_', ' ')}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>{count} ({pct}%)</Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: statusColors[status] || '#6b7280', borderRadius: 4, transition: 'width 0.5s' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>

              {/* Priority Breakdown */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>By Priority</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Object.entries(metrics.priorityMap).sort((a, b) => b[1] - a[1]).map(([priority, count]) => {
                    const total = Object.values(metrics.priorityMap).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <Box key={priority}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, textTransform: 'capitalize' }}>{priority}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>{count} ({pct}%)</Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: priorityColors[priority] || '#eab308', borderRadius: 4, transition: 'width 0.5s' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>

              {/* Category Breakdown */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>By Category</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Object.entries(metrics.categoryMap).sort((a, b) => b[1] - a[1]).map(([category, count]) => {
                    const total = Object.values(metrics.categoryMap).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    const colors = ['#6366f1', '#ec4899', '#14b8a6', '#f97316', '#8b5cf6', '#06b6d4'];
                    const color = colors[Object.keys(metrics.categoryMap).indexOf(category) % colors.length];
                    return (
                      <Box key={category}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, textTransform: 'capitalize' }}>{category}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: '#6b7280' }}>{count} ({pct}%)</Typography>
                        </Box>
                        <Box sx={{ height: 8, bgcolor: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 4, transition: 'width 0.5s' }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default HelpdeskReports;
